/**
 * Cloudflare Turnstile helper（注册/发码防刷）。
 *
 * 开关：VUE_APP_TURNSTILE_ENABLED=true 且配置了 SITE_KEY 才会拉脚本/出控件。
 * 与后端 auth.turnstile_enabled 对齐（Google/Email/发码）。WALLET 登录路径不调用本模块。
 * 后端关时前端也必须关，否则仍会出现「请验证您是真人」浮层。
 * 未开启时返回空 token（后端关闭校验时放行）。
 * Managed Widget：render 后自动出 token；勿强制 size=invisible / execution=execute（易空 token）。
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
let hostEl = null;
let widgetId = null;

export function getTurnstileSiteKey() {
  return String(process.env.VUE_APP_TURNSTILE_SITE_KEY || '').trim();
}

/** 是否打包了 site key（不等于产品开关） */
export function isTurnstileConfigured() {
  return Boolean(getTurnstileSiteKey());
}

/**
 * 产品开关：仅当显式 VUE_APP_TURNSTILE_ENABLED=true 且有 site key 时才出题。
 * 与后端 auth.turnstile_enabled 应对齐；后端关时前端也必须关，避免无用浮层。
 */
export function isTurnstileEnabled() {
  const flag = String(process.env.VUE_APP_TURNSTILE_ENABLED ?? '')
    .trim()
    .toLowerCase();
  return flag === 'true' && isTurnstileConfigured();
}

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-deepsage-turnstile]');
    if (existing) {
      const done = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('turnstile script loaded but api missing'));
      };
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.deepsageTurnstile = '1';
    s.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile script loaded but api missing'));
    };
    s.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function ensureHost() {
  if (hostEl && document.body.contains(hostEl)) return hostEl;
  hostEl = document.createElement('div');
  hostEl.id = 'deepsage-turnstile-host';
  hostEl.setAttribute('aria-hidden', 'true');
  hostEl.style.cssText =
    'position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000;';
  document.body.appendChild(hostEl);
  return hostEl;
}

function resetHostStyle() {
  if (!hostEl) return;
  hostEl.style.left = '50%';
  hostEl.style.bottom = '16px';
  hostEl.style.transform = 'translateX(-50%)';
  hostEl.style.opacity = '1';
  hostEl.style.pointerEvents = 'auto';
}

function hideHost() {
  if (!hostEl) return;
  hostEl.style.left = '-9999px';
  hostEl.style.bottom = '0';
  hostEl.style.transform = '';
  hostEl.style.opacity = '0';
  hostEl.style.pointerEvents = 'none';
}

export function buildTurnstileRenderOptions(siteKey, action, handlers = {}) {
  return {
    sitekey: siteKey,
    theme: 'auto',
    appearance: 'interaction-only',
    retry: 'auto',
    'refresh-expired': 'auto',
    callback: handlers.callback,
    'error-callback': handlers.error,
    'expired-callback': handlers.expired,
    'timeout-callback': handlers.timeout,
    // action 仅作遥测；过长会被拒
    action: String(action || 'auth').slice(0, 32),
  };
}

/**
 * @param {string} [action]
 * @returns {Promise<string>}
 */
export async function requestTurnstileToken(action = 'auth') {
  if (!isTurnstileEnabled()) return '';

  const siteKey = getTurnstileSiteKey();
  const turnstile = await loadTurnstileScript();
  if (!turnstile?.render) {
    throw new Error('turnstile api unavailable');
  }

  const host = ensureHost();
  resetHostStyle();
  if (widgetId != null && turnstile.remove) {
    try {
      turnstile.remove(widgetId);
    } catch {
      /* ignore */
    }
    widgetId = null;
  }
  host.innerHTML = '';

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      hideHost();
      fn(value);
    };

    const timer = setTimeout(() => {
      finish(reject)(new Error('turnstile timeout'));
    }, 25000);

    try {
      widgetId = turnstile.render(
        host,
        buildTurnstileRenderOptions(siteKey, action, {
          callback: (token) => finish(resolve)(String(token || '').trim()),
          error: () => finish(reject)(new Error('turnstile error')),
          expired: () => finish(reject)(new Error('turnstile expired')),
          timeout: () => finish(reject)(new Error('turnstile user timeout')),
        })
      );
    } catch (err) {
      finish(reject)(err);
    }
  });
}
